import {
  Component,
  ChangeDetectionStrategy,
  Input,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnInit,
} from '@angular/core';
import { ColorType } from '@grid/models/color-types.model';
import { FocusManagerService } from '@grid/services';
import { ColorService } from '@grid/services/color.service';
import { Constants } from '@grid/utils/constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ColorEvent, HSLA } from 'ngx-color';
import {
  CellComponent,
  CellContext,
  ColorOptions,
  GridData,
} from '../../models';

@UntilDestroy()
@Component({
  selector: 'app-color-picker',
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorPickerComponent
  implements CellComponent<GridData>, OnInit, OnChanges, AfterViewInit
{
  @Input({ required: true }) context!: CellContext<GridData>;
  @Input({ required: true }) options: ColorOptions;
  @Input() isEditMode: boolean;
  @Input() isExternalSave = false;

  @Output() colorSelected = new EventEmitter<number>();

  colors: string[] = [];
  colorPalette: ColorType[];
  // the initial value set by the library
  currentColor: HSLA | string = {
    h: 250,
    s: 0.5,
    l: 0.2,
    a: 1,
  };

  constructor(
    private focusManager: FocusManagerService,
    private colorService: ColorService
  ) {}

  //#region Getters

  private get cellColor(): ColorType {
    const column = this.context.column.columnDef;
    const colorId = this.context.data[this.colorChangesDef][column];

    return this.colorPalette.find((x) => x.id === colorId);
  }

  private get colorChangesDef(): string {
    return this.options.colorPickerColumnDef;
  }

  //#endregion

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.context?.currentValue) {
      this.colorPalette = [
        Constants.DefaultValues.colorReset,
        ...this.options.colorPalette,
      ];
      this.colors = this.colorPalette.map((x) => x.hexCode);
    }

    if (changes.isEditMode?.currentValue) {
      setTimeout(() => this.removeCircleTooltips());
    }
  }

  ngOnInit(): void {
    const colors = this.context.data[this.colorChangesDef];
    const selectedColor = colors[this.context.column.columnDef];
    if (selectedColor) {
      this.currentColor = this.colors[selectedColor];
    }
  }

  ngAfterViewInit(): void {
    this.context.cell.actions?.setDelegateEditState(true);
    setTimeout(() => {
      this.focusManager.focused
        .pipe(untilDestroyed(this))
        .subscribe((element) => {
          if (element !== this.context.cell.element && !this.isEditMode) {
            this.context.cell.actions?.setDelegateEditState(false);
            this.context.cell.actions?.endEdit();
          }
        });
    });
  }

  changeComplete($event: ColorEvent): void {
    const colorId = this.colorPalette.find(
      (x) => x.hexCode === $event.color.hex
    )?.id;

    if (this.cellColor?.hexCode === $event.color.hex) {
      this.colorSelected.emit(colorId);
      return;
    }

    if (!this.isExternalSave) {
      this.updateContextData($event.color.hex);

      setTimeout(() => {
        this.context.cell.actions.onEdited();
      });
    }

    this.context.cell.actions?.setDelegateEditState(false);

    this.colorService.colorCell$.next({
      dataId: this.context.data.id,
      columnDef: this.context.column.columnDef,
      color: this.cellColor,
    });

    this.colorSelected.emit(colorId);
  }

  //#region Private methods

  private updateContextData(colorHex: string): void {
    const column = this.context.column.columnDef;
    const cellColors = this.context.data[this.colorChangesDef];

    cellColors[column] =
      this.colorPalette.find((x) => x.hexCode === colorHex)?.id ?? null;

    this.context.data[this.colorChangesDef] = cellColors;
  }

  private removeCircleTooltips(): void {
    const circleElements = document.getElementsByClassName('swatch');

    Array.from(circleElements).forEach((x) => x.removeAttribute('title'));
  }

  //#endregion
}
